import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';

import { ApiError, apiRequest } from '../lib/api-client';

type PaymentMethod = 'CASH' | 'QRIS' | 'TRANSFER';

type ProductOption = {
  id: string;
  name: string;
  sku: string;
  price: number;
  quantity: number;
};

type CustomerOption = {
  id: string;
  name: string;
  phone: string;
};

type Sale = {
  id: string;
  number: string;
  buyerName: string;
  paymentMethod: PaymentMethod;
  total: number;
  grossProfit: number;
  notes?: string;
  createdAt: string;
  items: Array<{
    quantity: number;
    product: { id: string; name: string; sku: string };
  }>;
  createdBy?: { name: string };
};

const PAGE_SIZE = 8;

const currency = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
});

const paymentLabel: Record<PaymentMethod, string> = {
  CASH: 'Tunai',
  QRIS: 'QRIS',
  TRANSFER: 'Transfer',
};

export function Sales({
  canWrite,
  canSelectCustomer,
  outletReady,
  onSaleCompleted,
}: {
  canWrite: boolean;
  canSelectCustomer: boolean;
  outletReady: boolean;
  onSaleCompleted: () => void;
}) {
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    customerId: '',
    productId: '',
    quantity: '1',
    paymentMethod: 'CASH' as PaymentMethod,
    buyerName: '',
    notes: '',
  });

  const selectedProduct = useMemo(
    () => products.find((product) => product.id === form.productId),
    [products, form.productId],
  );
  const parsedQuantity = Number(form.quantity) || 0;
  const totalPrice = (selectedProduct?.price ?? 0) * parsedQuantity;

  async function load() {
    if (!outletReady) return;
    setLoading(true);
    try {
      const [inventoryResult, salesResult, customerResult] = await Promise.all([
        canWrite
          ? apiRequest<{ data: ProductOption[] }>('/inventory?limit=100&page=1')
          : Promise.resolve({ data: [] as ProductOption[] }),
        apiRequest<{ data: Sale[]; meta: { total: number } }>(
          `/sales?limit=${PAGE_SIZE}&page=${page}`,
        ),
        canSelectCustomer
          ? apiRequest<{ data: CustomerOption[] }>('/customers?status=ACTIVE&limit=100&page=1')
          : Promise.resolve({ data: [] as CustomerOption[] }),
      ]);
      setProducts(inventoryResult.data);
      setSales(salesResult.data);
      setTotal(salesResult.meta.total);
      setCustomers(customerResult.data);
      setError('');
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Data penjualan gagal dimuat.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [outletReady, page]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const quantity = Number(form.quantity);
    if (!selectedProduct) {
      setError('Pilih produk yang akan dijual.');
      return;
    }
    if (!Number.isInteger(quantity) || quantity < 1) {
      setError('Jumlah harus berupa angka bulat minimal 1.');
      return;
    }
    if (quantity > selectedProduct.quantity) {
      setError(`Stok tidak cukup. Stok tersedia ${selectedProduct.quantity}.`);
      return;
    }
    if (!form.buyerName.trim()) {
      setError('Nama pembeli wajib diisi.');
      return;
    }

    setBusy(true);
    setError('');
    setMessage('');
    try {
      const sale = await apiRequest<Sale>('/sales', {
        method: 'POST',
        body: JSON.stringify({
          customerId: form.customerId || undefined,
          productId: selectedProduct.id,
          quantity,
          paymentMethod: form.paymentMethod,
          buyerName: form.buyerName.trim(),
          notes: form.notes.trim() || undefined,
        }),
      });
      setMessage(`Penjualan ${sale.number} berhasil disimpan.`);
      setForm({
        customerId: '',
        productId: '',
        quantity: '1',
        paymentMethod: 'CASH',
        buyerName: '',
        notes: '',
      });
      if (page !== 1) setPage(1);
      else await load();
      onSaleCompleted();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Penjualan gagal disimpan.');
    } finally {
      setBusy(false);
    }
  }

  if (!outletReady) {
    return (
      <div className="rounded-2xl bg-amber-50 p-6 text-amber-800">
        Belum ada outlet aktif. Pilih outlet sebelum mencatat penjualan.
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section className="page-shell">
      <p className="font-bold text-ajisai-700">Transaksi Outlet</p>
      <h1 className="mt-1 text-3xl font-black tracking-tight">Penjualan</h1>
      <p className="mt-2 text-sm text-slate-500">
        Pilih produk, isi jumlah dan pembayaran, lalu simpan transaksi.
      </p>

      {error && (
        <p role="alert" className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </p>
      )}
      {message && (
        <p className="mt-5 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-700">{message}</p>
      )}

      <div className="mt-6 grid items-start gap-6 xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        <form
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
          onSubmit={(event) => void submit(event)}
        >
          <h2 className="text-lg font-black text-slate-900">Transaksi Baru</h2>
          <div className="mt-5 space-y-5">
            {canSelectCustomer && (
              <label className="block text-sm font-bold text-slate-700">
                Customer Terdaftar <span className="font-normal text-slate-400">(opsional)</span>
                <select
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal outline-none focus:border-ajisai-600 focus:ring-2 focus:ring-ajisai-100"
                  value={form.customerId}
                  onChange={(event) => {
                    const customer = customers.find((item) => item.id === event.target.value);
                    setForm({
                      ...form,
                      customerId: event.target.value,
                      buyerName: customer?.name ?? form.buyerName,
                    });
                  }}
                >
                  <option value="">Pelanggan umum / isi nama manual</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} — {customer.phone}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="block text-sm font-bold text-slate-700">
              Produk
              <select
                className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal outline-none focus:border-ajisai-600 focus:ring-2 focus:ring-ajisai-100"
                required
                value={form.productId}
                onChange={(event) => setForm({ ...form, productId: event.target.value })}
              >
                <option value="">Pilih produk</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id} disabled={product.quantity === 0}>
                    {product.name} — stok {product.quantity}
                  </option>
                ))}
              </select>
            </label>

            {selectedProduct && (
              <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4 text-sm">
                <div>
                  <p className="text-slate-500">Harga jual</p>
                  <p className="mt-1 font-black">{currency.format(selectedProduct.price)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Stok tersedia</p>
                  <p className="mt-1 font-black">{selectedProduct.quantity}</p>
                </div>
              </div>
            )}

            <label className="block text-sm font-bold text-slate-700">
              Jumlah
              <input
                className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-lg font-bold outline-none focus:border-ajisai-600 focus:ring-2 focus:ring-ajisai-100"
                type="number"
                min="1"
                max={selectedProduct?.quantity}
                step="1"
                required
                value={form.quantity}
                onChange={(event) => setForm({ ...form, quantity: event.target.value })}
              />
            </label>

            <fieldset>
              <legend className="text-sm font-bold text-slate-700">Metode Pembayaran</legend>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {(Object.keys(paymentLabel) as PaymentMethod[]).map((method) => (
                  <label
                    key={method}
                    className={`cursor-pointer rounded-xl border px-3 py-3 text-center text-sm font-bold ${
                      form.paymentMethod === method
                        ? 'border-ajisai-600 bg-ajisai-50 text-ajisai-800'
                        : 'border-slate-300 text-slate-600'
                    }`}
                  >
                    <input
                      className="sr-only"
                      type="radio"
                      name="paymentMethod"
                      value={method}
                      checked={form.paymentMethod === method}
                      onChange={() => setForm({ ...form, paymentMethod: method })}
                    />
                    {paymentLabel[method]}
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="block text-sm font-bold text-slate-700">
              Nama Pembeli
              <input
                className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal outline-none focus:border-ajisai-600 focus:ring-2 focus:ring-ajisai-100"
                maxLength={100}
                placeholder="Contoh: Kak Rina"
                required
                readOnly={Boolean(form.customerId)}
                value={form.buyerName}
                onChange={(event) => setForm({ ...form, buyerName: event.target.value })}
              />
            </label>

            <label className="block text-sm font-bold text-slate-700">
              Catatan <span className="font-normal text-slate-400">(opsional)</span>
              <textarea
                className="mt-2 min-h-20 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal outline-none focus:border-ajisai-600 focus:ring-2 focus:ring-ajisai-100"
                maxLength={500}
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </label>
          </div>

          <div className="mt-6 flex items-center justify-between rounded-xl bg-ajisai-900 px-4 py-4 text-white">
            <span className="text-sm text-ajisai-100">Total</span>
            <strong className="text-xl">{currency.format(totalPrice)}</strong>
          </div>

          <button
            className="mt-4 w-full rounded-xl bg-ajisai-700 px-5 py-3 font-bold text-white shadow-lg shadow-ajisai-700/20 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canWrite || busy || !selectedProduct || selectedProduct.quantity === 0}
          >
            {busy ? 'Menyimpan...' : 'Simpan Penjualan'}
          </button>
          {!canWrite && (
            <p className="mt-3 text-center text-xs text-slate-500">
              Akun ini hanya memiliki akses melihat penjualan.
            </p>
          )}
        </form>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-5">
            <h2 className="font-black text-slate-900">Transaksi Terbaru</h2>
            <p className="mt-1 text-sm text-slate-500">{total} transaksi pada outlet ini.</p>
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3">Transaksi</th>
                  <th className="px-5 py-3">Produk</th>
                  <th className="px-5 py-3">Pembayaran</th>
                  <th className="px-5 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sales.map((sale) => (
                  <tr key={sale.id}>
                    <td className="px-5 py-4">
                      <p className="font-bold text-slate-900">{sale.buyerName}</p>
                      <p className="mt-1 text-xs text-slate-500">{sale.number}</p>
                      <p className="mt-1 text-xs text-slate-400">
                        {new Date(sale.createdAt).toLocaleString('id-ID')}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      {sale.items.map((item) => (
                        <p key={item.product.id}>
                          {item.product.name} × {item.quantity}
                        </p>
                      ))}
                    </td>
                    <td className="px-5 py-4">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700">
                        {paymentLabel[sale.paymentMethod]}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right font-black">
                      {currency.format(sale.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-100 md:hidden">
            {sales.map((sale) => (
              <article className="p-4" key={sale.id}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-slate-900">{sale.buyerName}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{sale.number}</p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700">
                    {paymentLabel[sale.paymentMethod]}
                  </span>
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  {sale.items[0]?.product.name} × {sale.items[0]?.quantity}
                </p>
                <div className="mt-3 flex items-end justify-between">
                  <p className="text-xs text-slate-400">
                    {new Date(sale.createdAt).toLocaleString('id-ID')}
                  </p>
                  <p className="font-black">{currency.format(sale.total)}</p>
                </div>
              </article>
            ))}
          </div>

          {!loading && sales.length === 0 && (
            <div className="px-5 py-14 text-center">
              <p className="font-bold text-slate-700">Belum ada transaksi</p>
              <p className="mt-1 text-sm text-slate-500">
                Penjualan yang berhasil akan tampil di sini.
              </p>
            </div>
          )}
          {loading && (
            <p className="px-5 py-12 text-center text-sm text-slate-500">Memuat transaksi...</p>
          )}

          <div className="flex items-center justify-between border-t border-slate-200 px-5 py-4 text-sm">
            <span className="text-slate-500">
              Halaman {page} dari {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                className="rounded-lg border px-3 py-2 disabled:opacity-40"
                disabled={page === 1}
                onClick={() => setPage((value) => value - 1)}
              >
                Sebelumnya
              </button>
              <button
                className="rounded-lg border px-3 py-2 disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => setPage((value) => value + 1)}
              >
                Berikutnya
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
